import React, { ChangeEvent, Component, ReactNode } from 'react';
import { ChartState } from 'redux/reducers/ChartReducer';
import CustomLoading from '../../../custom/CustomLoading';
import { Slider, SliderChangeParams } from 'primereact/slider';
import { RootState } from '../../../../redux/store';
import { changeChart, changeChartEditorOptions } from '../../../../redux/actions/ChartActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { OverlayPanel } from 'primereact/overlaypanel';
import Icon from '../../../../icons/Icon';
import DataType from '../../../../constant/DataType';
import { CustomClusterRange, CustomClusterRangeItem, RangeClusterRange, SegmentAppearance } from '../../../../domain/chart/editor/Segment';
import { SegmentItem } from '../../../../domain/chart/ChartDomains';
import { FormicaSelectBoxItem } from 'components/custom/FormicaSearchSelectBox';
import FormicaSearchSelectBox from '../../../custom/FormicaSearchSelectBox';
import FormicaInput from 'components/custom/FormicaInput';
import ChartValueFactory from '../../../../util/ChartValueFactory';
import { User } from '../../../../domain/rbac/RbacDomains';
import LanguageUtil from '../../../../util/LanguageUtil';

class SegmentClustering extends Component<Props, State> {
  public state = new State();

  render() {
    const { segmentItem } = this.props;
    const clustering = segmentItem?.segments.clustering || null;
    if (clustering == null) return <CustomLoading />;
    return (
      <div className={'w-full h-full flex flex-col gap-y-5'}>
        <div className={'flex flex-col w-full'}>
          <label className={'text-sm text-f-black-2'}>Field</label>
          <FormicaSearchSelectBox
            value={clustering.field.name}
            rounded={false}
            customSize={'medium'}
            onChange={(e: FormicaSelectBoxItem) => {
              this.onChangeField(e);
            }}
            options={this.getAvailableElasticVariables()}
            placeholder={LanguageUtil.get({ id: 'common.input.placeholder.availableFields', defaultMessage: 'Available Fields' })}
          />
        </div>

        <div>
          {this.getClusteringRange()}
          {clustering.range.type == 'range' && (
            <p
              onClick={() => {
                this.setRangeOption('custom');
              }}
              className={'text-f-purple text-xs mt-3 cursor-pointer'}
            >
              Create Custom Ranges
            </p>
          )}
        </div>
      </div>
    );
  }

  private getAvailableElasticVariables = () => {
    const {
      chart: { chartEditorOptions },
      user,
    } = this.props;
    const elasticVariables = ChartValueFactory.initializeElasticVariablesForChartMetricAndSegment(user?.activeRiskType as string).filter((field) => {
      return field.dataType == DataType.NUMBER;
    });
    if (chartEditorOptions.transactionVariables && Array.isArray(chartEditorOptions.transactionVariables)) {
      const variables = chartEditorOptions.transactionVariables
        ?.filter((field) => {
          return field.dataType == DataType.NUMBER;
        })
        .map((field) => {
          return {
            label: field.label,
            value: field.value,
            dataType: field.dataType,
          };
        });
      return [...elasticVariables, ...variables];
    }
    return [] as FormicaSelectBoxItem[];
  };

  private onChangeField = (field: FormicaSelectBoxItem) => {
    const { segmentItem, onChangeSegmentItem } = this.props;
    const clustering = segmentItem?.segments?.clustering || null;
    const appearance: SegmentAppearance | null = segmentItem?.segments?.clustering?.appearance || null;
    if (clustering) {
      clustering.field.name = field.value;
      clustering.field.dataType = field.dataType as DataType;
      segmentItem.name = `${field.label}`;
      if (appearance) {
        appearance.name = `${field.label}`;
      }
      onChangeSegmentItem(segmentItem);
    }
  };

  private setRangeOption = (rangeType: 'custom' | 'range') => {
    const { segmentItem, onChangeSegmentItem } = this.props;
    const clustering = segmentItem?.segments?.clustering || null;
    if (clustering) {
      const range: CustomClusterRange = {
        type: rangeType,
        items: [
          {
            from: 0,
            to: 100,
            label: 'New Range',
          },
        ],
      };
      clustering.range = range;
      onChangeSegmentItem(segmentItem);
    }
  };

  private getClusteringRange(): ReactNode {
    const { segmentItem } = this.props;
    const rangeOption = segmentItem?.segments?.clustering?.range || null;
    if (rangeOption != undefined) {
      const { maxRange, minRange } = this.state;
      if (rangeOption.type == 'range') {
        return (
          <div className={'flex flex-col gap-y-3'}>
            <label htmlFor={'rangeSlider'} className={'text-sm text-f-black-2'}>
              Clustering Range
            </label>
            <Slider
              id={'rangeSlider'}
              min={minRange}
              max={maxRange}
              value={(rangeOption as RangeClusterRange).interval}
              onChange={(e: SliderChangeParams) => {
                this.onChangeClusterRange(e);
              }}
            />
          </div>
        );
      }
      return this.customRanges();
    }
  }

  private onChangeClusterRange = (e: SliderChangeParams) => {
    const { segmentItem } = this.props;
    const rangeOption = segmentItem?.segments?.clustering?.range || null;
    const range: RangeClusterRange = rangeOption as RangeClusterRange;
    range.interval = e.value as number;
    this.props.onChangeSegmentItem(segmentItem);
  };

  private addCustomRange = (): void => {
    const { segmentItem, onChangeSegmentItem } = this.props;
    const clustering = segmentItem?.segments?.clustering || null;
    if (clustering) {
      const customRange: CustomClusterRangeItem = {
        label: '',
        from: 0,
        to: 0,
      };
      const range: CustomClusterRange = clustering.range as CustomClusterRange;
      range.items = [...range.items, customRange];
      onChangeSegmentItem(segmentItem);
    }
  };
  private removeClusterRangeItem = (index: number): void => {
    const { segmentItem, onChangeSegmentItem } = this.props;
    const clustering = segmentItem?.segments?.clustering || null;
    if (clustering) {
      const range: CustomClusterRange = clustering.range as CustomClusterRange;
      range.items.splice(index, 1);
      onChangeSegmentItem(segmentItem);
    }
  };

  private onChangeCustomClusterRangeItemFrom = (index: number, value: number) => {
    const { segmentItem, onChangeSegmentItem } = this.props;
    const clustering = segmentItem?.segments?.clustering || null;
    if (clustering) {
      const range: CustomClusterRange = clustering.range as CustomClusterRange;
      range.items[index].from = value;
      onChangeSegmentItem(segmentItem);
    }
  };

  private onChangeCustomClusterRangeItemTo = (index: number, value: number) => {
    const { segmentItem, onChangeSegmentItem } = this.props;
    const clustering = segmentItem?.segments?.clustering || null;
    if (clustering) {
      const range: CustomClusterRange = clustering.range as CustomClusterRange;
      range.items[index].to = value;
      onChangeSegmentItem(segmentItem);
    }
  };

  private onChangeCustomClusterRangeItemLabel = (index: number, value: string) => {
    const { segmentItem, onChangeSegmentItem } = this.props;
    const clustering = segmentItem?.segments?.clustering || null;
    if (clustering) {
      const range: CustomClusterRange = clustering.range as CustomClusterRange;
      range.items[index].label = value;
      onChangeSegmentItem(segmentItem);
    }
  };

  private customRanges = () => {
    const { segmentItem } = this.props;
    const clustering = segmentItem?.segments?.clustering || null;
    if (clustering == null) {
      return <CustomLoading />;
    }
    const range: CustomClusterRange = clustering.range as CustomClusterRange;
    return (
      <div className={'flex flex-col gap-y-4 py-2'}>
        <div className={'flex w-full  items-center justify-between'}>
          <p className={'text-f-black-2 text-[11px]'}>Ranges</p>
          <p onClick={() => this.setRangeOption('range')} className={'text-red-700 text-[11px] cursor-pointer'}>
            x Remove Custom Ranges
          </p>
        </div>
        {range.items?.map((item: CustomClusterRangeItem, index: number) => {
          let overlayRef: OverlayPanel | null = null;
          return (
            <div className={'flex group items-center  justify-between w-full gap-x-2 px-2 py-2 h-[32px] border-[0.5px]'} key={index}>
              <div className={'flex items-center cursor-pointer'}>
                <div onClick={(e) => overlayRef?.toggle(e)}>{item.from}</div>
                <Icon icon={'arrow-right'} className={'text-f-black-3'} size={16} />
                <div onClick={(e) => overlayRef?.toggle(e)}>{item.to}</div>
              </div>
              <div onClick={() => this.removeClusterRangeItem(index)} className={'hover:text-primary cursor-pointer'}>
                <Icon icon={'close'} className={'hover:text-primary'} size={16} />
              </div>
              <OverlayPanel onClick={(e) => e.stopPropagation()} ref={(ref) => (overlayRef = ref)}>
                <div className={'flex w-full px-2 py-2 gap-x-2'}>
                  <div className={'flex flex-col gap-y-2'}>
                    <div className={'flex w-full gap-x-2'}>
                      <div className={'w-20'}>
                        <label htmlFor={'custom-range-from'} className={'text-primary text-xs'}>
                          From
                        </label>
                        <FormicaInput
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            this.onChangeCustomClusterRangeItemFrom(index, parseInt(e.currentTarget.value));
                          }}
                          value={item.from}
                          id={'custom-range-from'}
                          type={'number'}
                        />
                      </div>
                      <div className={'w-20'}>
                        <label htmlFor={'custom-range-to'} className={'text-primary text-xs'}>
                          To
                        </label>
                        <FormicaInput
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            this.onChangeCustomClusterRangeItemTo(index, parseInt(e.currentTarget.value));
                          }}
                          value={item.to}
                          id={'custom-range-to'}
                          type={'number'}
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor={'custom-range-label'} className={'text-primary text-xs'}>
                        Label
                      </label>
                      <FormicaInput
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          this.onChangeCustomClusterRangeItemLabel(index, e.currentTarget.value);
                        }}
                        value={item.label}
                        id={'custom-range-label'}
                        type={'text'}
                      />
                    </div>
                  </div>
                </div>
              </OverlayPanel>
            </div>
          );
        })}
        <a onClick={this.addCustomRange} className={'px-2 py-2 bg-white border-[1px] border-f-purple text-f-purple w-full rounded cursor-pointer'}>
          + Add Range
        </a>
      </div>
    );
  };
}

interface Props extends ReduxProps {
  segmentItem: SegmentItem;
  onChangeSegmentItem: (segmentItem: SegmentItem) => void;
}

class State {
  maxRange: number = 100;
  minRange: number = 0;
}

const mapStateToProps = (state: RootState) => ({
  chart: state.chart as ChartState,
  user: state.auth.user as User,
});

const mapDispatchToProps = { changeChart, changeChartEditorOptions };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(SegmentClustering);
